/**
 * 简体中文 — Simplified, not Traditional.
 *
 * This market (Malaysia, Singapore, mainland-facing SEA) reads Simplified. A
 * Traditional bundle would be a separate locale rather than a substitute, which
 * is why the code is plain `zh` and matches zh-CN, zh-SG and zh-Hans by prefix.
 *
 * House terms follow what players on comparable sites already read: 存款 for
 * deposit, 提款 for withdrawal, 流水 for turnover/rollover, 优惠 for bonus in
 * the promotional sense and 红利 for the balance itself.
 */
export default {
  common: {
    loading: "加载中…",
    cancel: "取消",
    save: "保存",
    saving: "保存中…",
    close: "关闭",
    back: "返回",
    copy: "复制",
    copied: "已复制",
    all: "全部",
    max: "最大",
    optional: "选填",
    somethingWrong: "出了点问题，请再试一次。",
    signIn: "登录",
    register: "注册",
    signOut: "退出登录",
  },

  nav: {
    lobby: "游戏",
    money: "财务",
    promotions: "优惠",
    vip: "VIP",
    rebate: "返水",
    invite: "邀请好友",
    winLoss: "输赢报表",
    account: "我的账户",
    language: "语言",
  },

  menu: {
    soon: "敬请期待",
    notSignedIn: "未登录",
    menu: "菜单",
    closeMenu: "关闭菜单",

    play: "游戏",
    money: "财务",
    rewards: "奖励",
    account: "账户",
    help: "帮助",

    games: "游戏大厅",
    bonuses: "优惠活动",
    moneyPage: "财务",
    rebate: "返水比例",
    invite: "邀请好友",
    vip: "VIP",
    leaderboard: "排行榜",
    myAccount: "我的账户",
    winLoss: "输赢报表",
    howItWorks: "使用指南",
    contact: "帮助与联系",
  },

  footer: {
    help: "帮助",
    legal: "条款",
    terms: "条款与细则",
    privacy: "隐私政策",
    deposits: "存款说明",
    withdrawals: "提款说明",
    responsible: "仅限18岁以上。请理性游戏。",
  },

  authGate: {
    title: "请先登录",
    body: "您可以随意浏览。但要游戏、存款或领取任何优惠，都需要一个账户 — 注册不到一分钟。",
    createAccount: "注册账户",
  },

  prompt: {
    titleAction: "登录后即可{action}",
    title: "请先登录",
    body: "这需要一个 {site} 的账户。注册只需一分钟，之后会直接回到这里。",
    createAccount: "注册账户",
    haveOne: "我已有账户",
    keepLooking: "先随便看看",
    us: "我们",
    playGame: "进入 {game}",
  },

  auth: {
    signInToPlay: "登录后开始游戏",
    username: "用户名",
    password: "密码",
    tabUsername: "用户名",
    tabEmail: "邮箱",
    tabPhone: "手机",
    emailPlaceholder: "you@example.com",
    phonePlaceholder: "您的手机号码",
    sessionExpired: "登录已过期，请重新登录。",
    signingIn: "登录中…",
    forgotPassword: "忘记密码？",
    createAccount: "注册账户",

    resetTitle: "重置密码",
    resetSent: "如果该邮箱已注册，验证码已发送。",
    resetSentHint: "验证码10分钟内有效，且仅可使用一次。",
    enterMyCode: "输入验证码",
    backToSignIn: "返回登录",
    email: "电子邮箱",
    sending: "发送中…",
    sendResetCode: "发送验证码",

    newPasswordTitle: "设置新密码",
    passwordUpdated: "密码已更新，正在返回登录…",
    resetCode: "验证码",
    resetCodeHint: "我们发送到您邮箱的6位验证码，发送后10分钟内有效。",
    newPassword: "新密码",
    passwordRule: "至少10个字符。",
    confirmPassword: "确认密码",
    updating: "更新中…",
    updatePassword: "更新密码",
    requestNewCode: "重新获取验证码",
  },

  account: {
    title: "我的账户",
    sectionAccount: "账户",
    sectionSecurity: "安全",
    sectionPreferences: "偏好设置",

    verification: "身份验证",
    verified: "已验证",
    accountsOnFile: "已绑定 {count} 个银行账户。",
    accountsOnFilePlural: "已绑定 {count} 个银行账户。",
    beingChecked: "审核中",
    beingCheckedDetail: "通常几小时内完成。",
    notAccepted: "未通过",
    notAcceptedDetail: "请核对资料后重新提交。",
    notFinished: "未完成",
    notFinishedDetail: "资料已保存，但您尚未提交给我们。",
    notStarted: "未开始",
    notStartedDetail: "首次提款前需要完成，以便我们知道款项汇往何处。",
    manageBanks: "管理银行账户",
    addBank: "绑定银行账户",

    password: "密码",
    passwordIntro: "即使您已登录，我们仍会要求输入当前密码 — 登录状态并不能证明是谁在使用这台设备。",
    currentPassword: "当前密码",
    newPassword: "新密码",
    newPasswordHint: "至少10个字符。一句您记得住的短语，比一串短乱码更安全。",
    confirmNewPassword: "确认新密码",
    changePassword: "修改密码",

    currency: "货币",
    currencyIntro: "仅在余额为零且尚未产生任何交易前可更改。之后更改会改变过往对账单的含义，需由在线客服处理。",
    chooseCurrency: "选择货币",
    changeCurrency: "更改货币",
  },

  verifyEmail: {
    title: "验证您的邮箱地址",
    sentTo: "我们已将6位验证码发送至",
    enterIt: "输入验证码即可完成账户设置。",
    checking: "验证中…",
    verify: "验证",
    resending: "发送中…",
    resend: "重新发送验证码",
    useDifferent: "使用其他账户",
    expires: "验证码10分钟内有效。",
  },

  register: {
    title: "注册账户",
    email: "电子邮箱",
    emailHint: "我们会发送6位验证码进行确认。",
    signInInstead: "已有账户，去登录",
    sending: "发送中…",
    sendCode: "发送验证码",

    enterCodeSentTo: "请输入我们发送至以下邮箱的验证码",
    verificationCode: "验证码",
    checking: "验证中…",
    continue: "继续",
    useDifferentEmail: "使用其他邮箱",
    resend: "重新发送",

    username: "用户名",
    usernameHint: "可使用字母、数字和下划线。",
    password: "密码",
    passwordHint: "至少10个字符。",
    confirmPassword: "确认密码",
    fullName: "真实姓名",
    fullNameHint: "必须与银行账户开户名完全一致 — 用于存款与提款。",
    nickname: "昵称",
    nicknameHint: "其他玩家看到的名称。",
    phoneCode: "区号",
    phone: "手机号码",
    creating: "创建中…",
    createAccount: "创建账户",
    alreadyHave: "已经有账户了？",

    needsMore: "还需 {count} 个字符。",
    needsMorePlural: "还需 {count} 个字符。",
    passwordsDoNotMatch: "两次输入的密码不一致。",
    nicknameRequired: "请设置昵称，至少需要 3 个字母或数字。",
    phoneRequired: "请填写您的手机号码。",
    fullNameRequired: "请填写与您银行账户一致的真实姓名。",
  },

  bank: {
    title: "您的银行账户",
    intro: "提款只会转入您已保存并经我们核对的账户，绝不会转入提款时临时输入的账户。这正是确保您的资金只有您本人能取到的原因。",
    confirmed: "已确认",
    weCanPayAny: "以下账户均可收款",
    wePayInto: "款项将转入",
    pickAtWithdrawal: "提款时由您选择使用哪一个。所有账户都必须是您本人名下 — 这是我们添加前会核对的内容。",
    checkingNew: "我们正在核对您新增的账户。",
    carryOnMeantime: "在此期间，提款仍会照常转入上方已确认的账户。",
    spottedMistake: "发现填错了？请联系在线客服 — 提交后无法自行修改。",
    couldNotApprove: "您上次的变更未获通过。",
    maxAccounts: "您已保存上限 {count} 个账户。如需移除，请联系在线客服。",
    tryAgain: "重新提交",
    addAnother: "添加另一个银行账户",

    bankLabel: "银行",
    selectYourBank: "选择您的银行",
    otherTypeBelow: "其他 — 请在下方输入",
    bankNamePlaceholder: "银行名称",
    holderName: "开户人姓名",
    accountNumber: "银行账号",
    whyAdding: "添加原因",
    stillHasToBe: "仍须为 {name} — 可以换银行，但不能换人。",
    sending: "提交中…",
    sendForChecking: "提交审核",

    weAreChecking: "我们正在核对",
    sentAt: "已于 {when} 提交，通常几小时内完成。",
    couldNotConfirm: "我们无法确认此账户",
    checkAndRetry: "请核对银行资料后重新提交。",

    addYours: "绑定您的银行账户",
    neededBefore: "首次提款前需要完成，以便我们知道款项汇往何处。只需银行资料，无需上传任何文件。",
    starting: "开始中…",
    start: "开始",

    yourDetails: "您的银行资料",
    mustComeFrom: "存款须从此账户转出，提款也只会转回此账户。",
    hasToMatch: "必须与您账户上的姓名一致：",
    weWillSave: "我们会保存此姓名，并据此核对每一笔进出款项。",
    doesNotMatch: "与您账户上的姓名不一致。如果您的姓名已变更，请联系在线客服。",
    matches: "✓ 与您账户上的姓名一致。",
    finishLaterTitle: "保存资料但暂不提交",
    finishLater: "稍后再填",
  },

  misc: {
    siteUnavailable_title: "站点不可用",
    verifyYourEmail: "请验证您的邮箱",
    reasonGiven: "原因说明",
    createAccount: "注册账户",
    seeBonuses: "查看优惠",

    siteUnavailable: "本站点目前无法访问。请检查网址后重试。",
    notifications: "通知",
    needAttention: "有 {count} 项需要您处理",
    allCaughtUp: "暂无新通知。",
    qrAlt: "存款地址二维码",
    qrTooLong: "该地址过长，无法生成二维码 — 请直接复制。",
    youSubmitted: "您提交的资料",
    enterCodeWeSent: "请输入我们发送给您的6位验证码。",
    sentAgain: "已重新发送",
    resend: "重新发送",
  },

  support: {
    help: "帮助",
    tabChat: "在线咨询",
    tabFaq: "常见问题",
    tabContact: "联系我们",
    automatedNotice: "此处为自动回复。涉及您本人账户或资金的问题，请通过「联系我们」中的 WhatsApp 联系我们 — 那边有真人可以查看。",
    openWhatsApp: "打开 WhatsApp →",
    whatsappIntro: "WhatsApp 是联系我们最快的方式，也是唯一能查看您账户的渠道。",
    demoNumber: "演示号码 — 无人接听。上线前请在站点品牌设置中填入真实号码。",
  },

  legal: {
    notRealTitle: "这不是正式文件。",
    notRealBody: "这只是 {brand} 所需章节的大纲，并非经审阅的法律文本，对任何一方均无约束力。网站正式运营前，必须替换为针对本运营商、其牌照及其展业市场专门起草的条款。",
    englishOnly: "本草案仅以英文发布。待最终条款确定后会进行翻译 — 机器翻译的协议不构成协议。",
    version: "版本：未发布草案。无生效日期。",
    notFound: "未找到该文件。",
  },

  live: {
    title: "实时投注",
    playing: "{count} 人正在游戏",
    quiet: "目前没有人在游戏。",
    quietBody: "投注结算后会显示在这里。",
    stat_paid: "今日派彩",
    stat_biggest: "今日最高奖金",
    stat_rounds: "今日局数",
    topToday: "今日最高奖金",
    noWins: "今天还没有中奖记录。",
    won: "赢得",
    staked: "投注",
  },

  providers: {
    title: "合作游戏厂商",
    subtitle: "游戏由持牌厂商提供。",
  },

  trust: {
    licensed: "持牌运营",
    licensedBody: "依据条款中列明的牌照运营。",
    payouts: "当天出款",
    payoutsBody: "由专人审核，通常几分钟内完成。",
    support: "全天候客服",
    supportBody: "在线客服，全年无休。",
    fair: "结算可查",
    fairBody: "每一局都有记录，可供核查。",
  },

  help: {
    title: "使用指南",
    intro: "针对最常见问题的简短说明。",
    openIt: "前往",
    stepsLabel: "{count} 个步骤",

    deposit_title: "如何存款",
    deposit_body: "打开「财务」，先选择存入的币种，再选择支付方式。系统会显示收款账户或钱包地址，以及需要备注的参考号。转账完成后点击「我已付款」— 这只是将存款提交至审核队列，并不会立即入账。每一笔付款都由专人核对，通常只需几分钟。确认后余额会立即更新。",
    deposit_note: "款项必须来自您本人名下的账户。他人代付无法入账。",

    withdraw_title: "如何提款",
    withdraw_body: "请先绑定银行账户 — 我们只会转入您已绑定且通过核对的账户，绝不会转入提款时临时输入的账户。打开「财务」，选择「提款」，输入金额并确认。提交申请时金额即被冻结，会立即从可游戏余额中扣除。",
    withdraw_note: "银行账户上的姓名必须与您账户的姓名完全一致。申请处理前，您可随时在「记录」中取消，款项将退回。",

    bonus_title: "红利与现金",
    bonus_body: "红利在完成流水前不可提款。每笔红利都有各自的流水要求，发放时即已确定；投注会优先抵扣最早的一笔。红利完成流水后即转为现金，可与其他资金一样提款。",
    bonus_note: "「转账」页面会显示还需完成多少流水，以及有多少已可转出。",

    rebate_title: "返水说明",
    rebate_body: "您投注的每一笔都能获得返水，无论输赢 — 计算依据是投注额，而非亏损额。每日结算一次，并存入您的红利余额。返水比例取决于游戏厂商，您的VIP等级还会额外加成。",
    rebate_note: "各厂商的实时返水比例请见「返水」页面。",

    stillStuck: "还有疑问？",
    stillStuckBody: "在线客服全天候为您服务，并可查看您的账户。",
    contactUs: "联系客服",
  },

  lobby: {
    categories: {
      slots: "老虎机",
      live: "真人娱乐场",
      sports: "体育",
      fishing: "捕鱼",
      lottery: "4D彩票",
      cockfight: "斗鸡",
      horse: "赛马",
      esports: "电子竞技",
    },

    banners: {
      welcomeTitle: "迎新优惠",
      welcomeBody: "首存最高可获额外150%",
      cashbackTitle: "每日返现",
      cashbackBody: "每一局都能拿回一点",
      friendTitle: "邀请好友",
      friendBody: "双方都有奖励",
    },

    trust: {
      withdraw: "提款",
      withdrawValue: "当天到账",
      chat: "在线客服",
      chatValue: "全天候",
      payBy: "支付方式",
      payByValue: "银行或USDT",
    },

    popular: "热门游戏",
    notConnected: "该游戏尚未接入。",
    notConnectedTry: "该游戏尚未接入，请选择其他游戏。",
    showSlide: "显示第 {number} 张",
    dismiss: "关闭",
    winningNow: "正在开奖",
    pickAGame: "选择游戏",
    comingSoon: "即将推出",
    showAll: "查看全部",
    gamesOnTheWay: "更多游戏即将上线",
    hot: "热门",
    gameStudios: "游戏厂商",
    bonuses: "优惠活动",
    viewAll: "查看全部",
    bonus: "优惠",
  },

  vip: {
    title: "VIP",
    intro: "您的等级由累计存款总额决定，只升不降。",
    currentTier: "当前等级",
    depositedToDate: "累计存款",
    toReach: "距离 {tier}",
    topTier: "您已达到最高等级。",
    allTiers: "全部等级",
    tier: "等级",
    deposited: "存款要求",
    extraRebate: "额外返水",
    includes: "专属礼遇",
    you: "您",
    orMore: "{amount} 或以上",
    notPaidTitle: "等级返水加成尚未发放。",
    notPaidBody: "您的等级与存款从现在起开始记录，标准返水照常发放。等级加成上线后我们会在此说明。",
  },

  promotions: {
    title: "优惠活动",
    intro: "本站所有进行中的活动及完整条款。",
    hideTerms: "收起条款",
    readTerms: "查看条款",
    draftTitle: "以下条款为草案。",
    draftBody: "上方的数字、日期与厂商名称均为占位内容，正式推出任何优惠前会先行确认。规则本身 — 最低派发金额、VIP返水加成与除外类别 — 均已生效，并按所述发放。",
  },

  rebate: {
    title: "返水比例",
    intro: "每一局都有返水，无论输赢。",
    settingUp: "返水比例正在设置中，稍后将在此显示。",
    toYou: "归您所有",
    referral: "推荐",
    shareholder: "股东",
    supplier: "游戏厂商",
    you: "您",
    howItAddsUp: "如何计算",
    example: "返水按您的投注额计算，而非按输赢计算。",
    exampleFull: "返水按您的投注额计算，而非按输赢计算。在老虎机投注 {stake}，返水 {rate}，即可获得 {reward} — 无论这些局是赢是输。",
    exampleDaily: "每日结算一次，并存入您的红利余额。",
  },

  invite: {
    title: "邀请好友",
    intro: "分享您的邀请码。通过它注册的用户都会记在您名下。",
    yourCode: "您的邀请码",
    codeCopied: "已复制邀请码",
    copyCode: "复制邀请码",
    yourLink: "您的链接",
    joinedThroughYou: "通过您注册",
    namesNotShown: "统计自使用您邀请码注册的用户。我们不会展示他们的账户信息。",
    noRewardTitle: "推荐奖励尚未发放。",
    noRewardBody: "通过您邀请码的注册从现在起已开始记录，不会遗漏 — 但您今天获得的返水仅来自您本人的投注。相关政策变更后我们会在此说明。",
  },

  winLoss: {
    title: "输赢报表",
    intro: "仅统计已结算的局。仍在进行中的注单不计入任何一方。",
    from: "开始",
    to: "结束",
    ranges: { today: "今天", week: "近7天", month: "近30天" },
    youAreUp: "盈利",
    youAreDown: "亏损",
    staked: "投注额",
    returned: "返还",
    rounds: "局数",
    byGameType: "按游戏类型",
    dayByDay: "按日统计",
    empty: "该时间段内没有已结算的局。",
    roundsStaked: "{rounds} 局 · 投注 {staked}",
  },

  wallet: {
    title: "钱包",
    systemBalance: "主钱包余额",
    makeADeposit: "先存一笔即可开始。",
    setAside: "已冻结 {amount}",
    addMoney: "存款",
    withdraw: "提款",
    recentActivity: "最近记录",
    empty: "暂无记录。",
  },

  money: {
    title: "财务",
    available: "可用余额",
    held: "冻结中",
    heldHint: "对应正在审核的申请",
    bonus: "红利",
    bonusHint: "完成流水后才可提款",

    tabs: {
      deposit: "存款",
      transfer: "转账",
      withdraw: "提款",
      history: "记录",
      bank: "银行卡",
    },

    hints: {
      transfer: "红利转现金，或转给其他玩家",
      history: "所有存款、提款与账目变动",
      bank: "提款到账的银行账户",
    },
  },

  deposit: {
    noMethodsTitle: "暂无支付方式",
    noMethodsBody: "本站尚未开通存款方式。请联系客服为您的账户充值。",

    ticketNumber: "存款单 #{id}",
    network: "网络",
    sendToAddress: "请转入此地址",
    copyAddress: "复制地址",
    wrongNetwork: "请务必使用 {network} 网络转账。通过其他网络转出的资金无法找回。",

    cardPendingTitle: "信用卡支付仍在接入中。",
    cardPendingBody: "我们的团队会联系您完成这笔存款，您也可以取消后改用其他方式。",

    transferTo: "请转账 {amount} 至：",
    bank: "银行",
    accountName: "户名",
    accountNumber: "账号",
    swift: "SWIFT",
    branch: "分行",
    copyAccount: "复制账号",
    quoteReference: "请在转账备注中填写 {reference}，以便核对到您的账户。",
    transferToConfirmed: "请转账 {amount} 至我们客服与您确认的账户。",
    quoteReferenceGeneric: "请在转账时备注 {reference}，以便核对到您的账户。",

    expires: "此申请将于 {when} 失效。",
    yourReference: "您的付款凭证（选填）",
    yourReferencePlaceholder: "交易哈希或银行流水号",
    yourReferenceHint: "可加快审核 — 我们会用它来核对这笔付款。",
    iHavePaid: "我已付款",
    sending: "提交中…",

    checkingTitle: "已收到 — 我们正在核对您的付款。",
    checkingBody: "确认后余额会立即更新。目前尚未入账。",
    makeAnother: "再存一笔",

    payIn: "存入币种",
    chooseHow: "选择存款方式",
    recommended: "推荐",
    selected: "已选择",
    min: "最低 {amount}",
    max: "最高 {amount}",
    addressLabel: "{network} 地址",

    amount: "金额",
    otherAmount: "其他金额（{currency}）",
    aboveMaximum: "此方式单笔最高为 {amount}。",
    belowMinimum: "此方式最低为 {amount}。",
    youWillGet: "您大约将获得",
    rateIndicative: "仅供参考。实际汇率以确认您付款时的汇率为准。",
    rateStale: "该汇率已有一段时间未更新，可能已有变动。",
    rateUnavailable: "暂时无法获取 {pay} 汇率。您的存款将按确认时的汇率兑换为 {account}。",
    preparing: "处理中…",
    submit: "存款 {amount}",

    groups: { instant: "即时到账", bank: "银行与加密货币" },
  },

  withdraw: {
    doneTitle: "申请已受理。该金额已冻结，不再计入可游戏余额。",

    blockers: {
      statusTitle: "您的账户已被暂停",
      statusBody: "提款已暂停。请联系在线客服，我们会为您处理。",
      kycTitle: "请先告诉我们收款账户",
      kycBody: "先绑定您的银行账户 — 只需一分钟，无需上传任何文件。",
      kycAction: "绑定银行账户",
      emailTitle: "请先验证邮箱",
      emailBody: "请输入我们发送给您的6位验证码。",
      balanceTitle: "暂无可提款金额",
      balanceBody: "已为其他申请冻结的金额不能重复提款。",
      balanceAction: "去存款",
      dailyTitle: "今日次数已用完",
      dailyBody: "您今天已用完全部 {count} 次提款申请，明天可再次申请。",
      turnoverTitle: "就快好了",
      turnoverBody: "再完成 {amount} 流水即可提款。",
      turnoverAction: "去游戏",
    },

    sendAs: "提款方式",
    noMethods: "暂未开通提款方式。请联系在线客服，我们会协助您。",
    today: "今日提款次数",
    amount: "金额",
    walletAddress: "钱包地址",
    walletAddressPlaceholder: "您的 {network} 地址",
    walletAddressWarning: "必须是 {network} 地址。转入错误网络的资金我们无法找回。",

    whichAccount: "选择账户",
    paidIntoSaved: "将转入您已绑定的银行账户",
    noAccountSaved: "尚未绑定银行账户。",
    onlySavedAccounts: "提款只会转入您已绑定并通过审核的账户。",
    addAnother: "绑定另一个",
    ownName: "— 必须是您本人的账户。",

    holdTitle: "提交申请时资金即被冻结。",
    holdBody: "该金额会立即从可游戏余额中扣除；只有在您取消或我们无法批准时才会退回。申请处理前，您随时可在「记录」中取消。",
    holdTurnover: "此外，提款前您至少需要完成与存入金额相等的流水。",

    playRequirement: "流水要求",
    requesting: "提交中…",
    submit: "申请提款",
  },

  transfer: {
    cash: "现金",
    setAside: "已冻结 {amount}",
    bonus: "红利",

    modes: { bonus: "红利 → 现金", member: "转给玩家" },

    bonusIntro: "红利在完成流水前不可提款。转到现金的那一刻，它才真正属于您。",
    playedThrough: "已完成流水",
    amountToMove: "转账金额",
    moving: "转账中…",
    moveToCash: "转为现金",
    keepPlaying: "继续游戏即可解锁。",

    amount: "金额",
    sendTo: "转给",
    theirUsername: "对方的用户名",
    note: "备注",
    checkUsernameTitle: "请仔细核对用户名。",
    checkUsernameBody: "转账会直接进入对方余额，且无法撤回。",
    transfersLeft: "您今天还剩 {left} / {total} 次转账。",
    send: "转账",
  },

  history: {
    show: "显示",
    everything: "全部",
    moneyIn: "存入",
    moneyOut: "转出",
    from: "开始",
    to: "结束",

    ranges: {
      today: "今天",
      yesterday: "昨天",
      week: "近7天",
      month: "近30天",
    },

    empty: "该时间段内没有记录。请尝试扩大日期范围。",
    cancelHold: "取消申请并解冻",
    cancelling: "取消中…",
    requested: "申请金额 {amount}",
    shortBy: "少了",
    overBy: "多了",

    kind: { deposit: "存款", withdrawal: "提款" },

    status: {
      AwaitingConfirmation: "等待确认",
      Pending: "待处理",
      Completed: "已完成",
      Rejected: "已拒绝",
      Cancelled: "已取消",
      Expired: "已过期",
      Approved: "已批准",
      Processing: "处理中",
    },
  },
};
